import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useOV25UI } from '../../contexts/ov25-ui-context.js';
import { VariantsContent } from './VariantsContent.js';
import type { Variant } from './ProductVariants.js';
import { NoResults } from './NoResults.js';
import { DefaultVariantCard } from './variant-cards/DefaultVariantCard.js';
import { SizeVariantCard } from './variant-cards/SizeVariantCard.js';
import { VariantThumb } from './variant-cards/VariantThumb.js';
import { FilterControls } from './FilterControls.js';
import { FilterContent } from './FilterContent.js';
import { Snap2ModulesOptionBody } from './Snap2ModulesOptionBody.js';
import { Button } from '../ui/button.js';
import { CheckoutButton } from './CheckoutButton.js';
import { Snap2VariantSheetColumn } from '../Snap2VariantSheetColumn.js';
import {
  selectionBedSizeFromMetadata,
  type ConfiguratorSelectionBedMetadata,
} from '../../utils/configurator-utils.js';

const BATCH_SIZE = 12;
const SIZE_BATCH_SIZE = 4;
const DROPDOWN_STEP_THRESHOLD = 6;

export type WizardVariantsMode = 'inline' | 'drawer';

export interface WizardVariantsProps {
  mode: WizardVariantsMode;
}

export const WizardVariants: React.FC<WizardVariantsProps> = ({ mode }) => {
  const {
    variantPanelOptions,
    selectedSelections,
    handleSelectionSelect,
    getSelectedValue,
    range,
    products,
    currentProductId,
    applySearchAndFilters,
    buyNowFunction,
    addToBasketFunction,
    disableAddToCart,
    isSnap2Mode,
    activeOptionId,
    setActiveOptionId,
    getString,
  } = useOV25UI();

  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    const idx = variantPanelOptions.findIndex(o => o.id === activeOptionId);
    if (idx >= 0) setCurrentStep(idx);
  }, [activeOptionId, variantPanelOptions]);
  const [visibleCount, setVisibleCount] = useState<{ [optionId: string]: number }>({});
  const [isFilterOpen, setIsFilterOpen] = useState<{ [optionId: string]: boolean }>({});
  const [previousFilteredCounts, setPreviousFilteredCounts] = useState<{ [optionId: string]: number }>({});
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const sentinelRefs = useRef<{ [optionId: string]: HTMLDivElement | null }>({});

  const totalSteps = variantPanelOptions.length + 1;
  const isReviewStep = currentStep === totalSteps - 1;
  const currentOption = isReviewStep ? null : variantPanelOptions[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;

  const getBatchSize = (optionId: string) => optionId === 'size' ? SIZE_BATCH_SIZE : BATCH_SIZE;

  const loadMore = useCallback((optionId: string) => {
    setVisibleCount(prev => ({
      ...prev,
      [optionId]: (prev[optionId] || getBatchSize(optionId)) + getBatchSize(optionId)
    }));
  }, []);

  const goNext = () => {
    const nextStep = Math.min(currentStep + 1, totalSteps - 1);
    setCurrentStep(nextStep);
    setActiveOptionId(nextStep < variantPanelOptions.length ? variantPanelOptions[nextStep].id : null);
  };
  const goBack = () => {
    const prevStep = Math.max(currentStep - 1, 0);
    setCurrentStep(prevStep);
    setActiveOptionId(variantPanelOptions[prevStep]?.id ?? null);
  };

  const toggleFilter = (optionId: string) => {
    setIsFilterOpen(prev => ({
      ...prev,
      [optionId]: !prev[optionId]
    }));
  };

  useEffect(() => {
    variantPanelOptions.forEach(option => {
      if (option.id === 'modules') return;
      const filteredOption = option.id === 'size' ? option : applySearchAndFilters(option, option.id);
      const currentFilteredCount = filteredOption.groups?.flatMap(group => group.selections || []).length || 0;
      const previousCount = previousFilteredCounts[option.id] || 0;

      if (currentFilteredCount !== previousCount) {
        setVisibleCount(prev => ({
          ...prev,
          [option.id]: getBatchSize(option.id)
        }));
        setPreviousFilteredCounts(prev => ({
          ...prev,
          [option.id]: currentFilteredCount
        }));
      }
    });
  }, [variantPanelOptions, applySearchAndFilters, previousFilteredCounts]);

  useEffect(() => {
    setCurrentStep(s => Math.min(s, Math.max(0, totalSteps - 1)));
  }, [totalSteps]);

  useEffect(() => {
    if (mode !== 'drawer' || !rootRef.current) return;
    const el = rootRef.current;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setAvailableHeight(entry.contentRect.height);
    });
    ro.observe(el);
    setAvailableHeight(el.getBoundingClientRect().height);
    return () => ro.disconnect();
  }, [mode]);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    Object.entries(sentinelRefs.current).forEach(([optionId, el]) => {
      if (!el) return;
      const root = el.closest('.ov-wizard-variants-scroll');
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) loadMore(optionId);
        },
        { root: root || null, rootMargin: '80px', threshold: 0 }
      );
      observer.observe(el);
      observers.push(observer);
    });
    return () => observers.forEach(o => o.disconnect());
  }, [loadMore, currentStep, visibleCount]);

  if (variantPanelOptions.length === 0) return null;

  const filteredOption = !isReviewStep && currentOption
    ? (currentOption.id === 'size' || currentOption.id === 'modules'
      ? currentOption
      : applySearchAndFilters(currentOption, currentOption.id))
    : null;
  const allVariants = (filteredOption?.groups?.flatMap((group: any) =>
    group.selections?.map((selection: any) => ({
      id: selection.id,
      groupId: group.id,
      optionId: currentOption!.id,
      name: selection.name,
      bedSize: selectionBedSizeFromMetadata(selection as { metadata?: ConfiguratorSelectionBedMetadata }),
      price: selection.price,
      image: currentOption!.id === 'size'
        ? (selection.thumbnail || '/placeholder.svg?height=200&width=200')
        : (selection.miniThumbnails?.medium || '/placeholder.svg?height=200&width=200'),
      blurHash: selection.blurHash,
      data: currentOption!.id === 'size' ? products?.find(p => p?.id === selection?.id) : selection.data,
      isSelected: currentOption!.id === 'size'
        ? selection.id === currentProductId || selectedSelections.some(
            sel => sel.optionId === currentOption!.id && sel.selectionId === selection.id
          )
        : selectedSelections.some(
            sel => sel.optionId === currentOption!.id &&
              sel.groupId === group.id &&
              sel.selectionId === selection.id
          ),
      swatch: selection.swatch
    })) || []
  ) ?? []) as Variant[];

  const count = currentOption ? (visibleCount[currentOption.id] ?? getBatchSize(currentOption.id)) : 0;
  const visibleVariants = allVariants.slice(0, count);
  const hasMore = count < allVariants.length;
  const isFilterOpenForOption = currentOption ? (isFilterOpen[currentOption.id] || false) : false;

  const useDropdown = totalSteps > DROPDOWN_STEP_THRESHOLD;
  const hasAddToBasket = typeof addToBasketFunction === 'function' && !disableAddToCart;
  const hasBuyNow = typeof buyNowFunction === 'function';
  const hasCheckoutActions = hasAddToBasket || hasBuyNow;
  const reviewStepLabel = hasCheckoutActions
    ? getString('wizardReview', undefined, 'Review')
    : getString('wizardOverview', undefined, 'Overview');
  const getStepLabel = (idx: number) =>
    idx === totalSteps - 1 ? reviewStepLabel : variantPanelOptions[idx]?.name ?? '';
  const stepContentClasses = mode === 'inline'
    ? 'ov:flex ov:flex-col ov:bg-[var(--ov25-background-color)]'
    : 'ov:flex ov:flex-col ov:bg-[var(--ov25-background-color)]';

  const stepIndicatorBlock = (
    <div className="ov25-option-header ov:flex ov:flex-col ov:gap-2 ov:px-4 ov:py-3">
      {useDropdown ? (
        <div className="ov:relative">
          <select
            value={currentStep}
            onChange={(e) => {
              const step = Number(e.target.value);
              setCurrentStep(step);
              setActiveOptionId(step < variantPanelOptions.length ? variantPanelOptions[step].id : null);
            }}
            className="ov:w-full ov:appearance-none ov:bg-transparent ov:border ov:border-(--ov25-border-color) ov:rounded-md ov:px-3 ov:py-2 ov:text-sm ov:pr-8 ov:cursor-pointer ov:focus:outline-none ov:focus:ring-1 ov:focus:ring-(--ov25-primary-color)"
          >
            {variantPanelOptions.map((option, idx) => (
              <option key={option.id} value={idx}>{idx + 1}. {option.name}</option>
            ))}
            <option value={totalSteps - 1}>{totalSteps}. {reviewStepLabel}</option>
          </select>
          <ChevronDown size={16} className="ov:absolute ov:right-2 ov:top-1/2 ov:-translate-y-1/2 ov:pointer-events-none ov:text-(--ov25-secondary-text-color)" />
        </div>
      ) : (
        <div className="ov:grid ov:grid-cols-[1fr_auto_1fr] ov:items-center ov:gap-4 ov:min-h-[48px]">
          <div className="ov:flex ov:justify-start">
            {!isFirstStep && (
              <button
                onClick={goBack}
                className="ov:flex ov:items-center ov:gap-1 ov:text-sm ov:text-(--ov25-secondary-text-color) ov:opacity-70 ov:hover:opacity-100 ov:transition-opacity ov:cursor-pointer"
              >
                <ChevronLeft size={14} />
                {getString(
                  'wizardPreviousStep',
                  { STEP_LABEL: getStepLabel(currentStep - 1) },
                  getStepLabel(currentStep - 1),
                )}
              </button>
            )}
          </div>
          <div className="ov:flex ov:flex-col ov:items-center ov:justify-center ">
            <p className=" ov:text-(--ov25-secondary-text-color) ov:font-light ov:text-sm">
              {getString(
                'wizardStepProgress',
                { CURRENT_STEP: String(currentStep + 1), TOTAL_STEPS: String(totalSteps) },
                `Step ${currentStep + 1} of ${totalSteps}`,
              )}
            </p>
            <p className="ov:text-base ov:font-semibold ov:text-(--ov25-secondary-color)">
              {getString(
                'wizardCurrentStep',
                { STEP_LABEL: getStepLabel(currentStep) },
                getStepLabel(currentStep),
              )}
            </p>
          </div>
          <div className="ov:flex ov:justify-end">
            {!isLastStep && (
              <button
                onClick={goNext}
                className="ov:flex ov:items-center ov:gap-1 ov:text-sm ov:text-(--ov25-secondary-text-color) ov:opacity-70 ov:hover:opacity-100 ov:transition-opacity ov:cursor-pointer"
              >
                {getString(
                  'wizardNextStep',
                  { STEP_LABEL: getStepLabel(currentStep + 1) },
                  getStepLabel(currentStep + 1),
                )}
                <ChevronRight size={14} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const filterBlock = !isReviewStep && currentOption && currentOption.id !== 'size' && currentOption.id !== 'modules' && (
    <div className="ov:shrink-0 ov:h-(--ov25-wizard-variants-filter-height) ov:flex ov:items-center">
      <div className="ov:w-full">
        <FilterControls
          isFilterOpen={isFilterOpenForOption}
          setIsFilterOpen={() => toggleFilter(currentOption!.id)}
          isGrouped={false}
          optionId={currentOption!.id}
        />
      </div>
    </div>
  );

  const buttonsBlock = totalSteps > 1 && (
    <div className={`ov25-checkout-button-wrapper ov:shrink-0 ov:flex ov:items-center ov:justify-between ov:gap-3 ${isReviewStep ? '' : 'ov:px-4 ov:py-2'}`}>
      <button
        onClick={goBack}
        disabled={isFirstStep}
        className="ov:flex ov:items-center ov:gap-1 ov:px-3 ov:py-2 ov:text-sm ov:text-(--ov25-secondary-text-color) ov:disabled:opacity-30 ov:disabled:cursor-not-allowed ov:hover:bg-(--ov25-hover-color) ov:rounded ov:transition-colors"
      >
        <ChevronLeft size={18} />
        {getString('wizardBackButtonLabel', {}, 'Back')}
      </button>
      {isReviewStep && hasCheckoutActions ? (
        isSnap2Mode ? (
          <div className="ov:flex-1 ov:min-w-0" aria-hidden />
        ) : (
          <div className="ov:flex-1 ov:min-w-0">
            <CheckoutButton />
          </div>
        )
      ) : !isReviewStep ? (
        <Button
          onClick={goNext}
          variant="configure"
          className="ov:flex-1 ov:flex ov:items-center ov:justify-center  ov:gap-1 ov:uppercase ov:disabled:cursor-not-allowed"
        >
          {getString('wizardNextButtonLabel', {}, 'Next')}
          <ChevronRight size={18} />
        </Button>
      ) : null}
    </div>
  );

  const stepContentOnly = (
    <>
      {isReviewStep ? (
        <div
          data-ov25-wizard-variants-step-content
          className={`${stepContentClasses} ov25-variant-group-content`}
        >
          <div
              data-ov25-wizard-variants-content
              className="ov:flex ov:flex-col ov:min-h-0 ov:p-4 ov:overflow-y-auto"
            >
            <dl className="ov:space-y-3 ov:text-sm ov:flex-1">
              {variantPanelOptions.map((option, idx) => {
                let value = '';
                let imageUrl = '';
                let reviewGroupName = '';
                if (option.id === 'size') {
                  const sizeGroup = option.groups?.[0];
                  const sizeSelection = sizeGroup?.selections?.find((s: any) => s.id === currentProductId);
                  value = (range?.name ? range.name + ' ' : '') + (sizeSelection?.name || getSelectedValue(option));
                  imageUrl = sizeSelection?.thumbnail || '';
                  reviewGroupName = String((sizeGroup as { groupName?: string; name?: string } | undefined)?.groupName ?? (sizeGroup as { name?: string } | undefined)?.name ?? '');
                } else if (option.id === 'modules') {
                  value = '—';
                  imageUrl = '';
                  reviewGroupName = '';
                } else {
                  const sel = selectedSelections.find(s => s.optionId === option.id);
                  const group = option.groups?.find((g: any) => g.id === sel?.groupId);
                  const selection = group?.selections?.find((s: any) => s.id === sel?.selectionId);
                  const bs = selectionBedSizeFromMetadata(selection as { metadata?: ConfiguratorSelectionBedMetadata });
                  value =
                    selection?.name && bs ? `${selection.name} · ${bs}` : selection?.name || getSelectedValue(option);
                  imageUrl = (selection as any)?.miniThumbnails?.medium || selection?.thumbnail || '';
                  reviewGroupName = String((group as { groupName?: string; name?: string } | undefined)?.groupName ?? (group as { name?: string } | undefined)?.name ?? '');
                }
                const reviewStepStringVars = {
                  OPTION_NAME: option.name,
                  GROUP_NAME: reviewGroupName,
                  SELECTION_LABEL: value || '—',
                };
                return (
                  <div key={option.id} className="ov:flex ov:items-center ov:gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentStep(idx);
                        setActiveOptionId(variantPanelOptions[idx]?.id ?? null);
                      }}
                      className="ov:flex ov:items-center ov:gap-3 ov:w-full ov:text-left ov:cursor-pointer ov:hover:bg-(--ov25-hover-color) ov:rounded-md ov:p-2 ov:-m-2 ov:transition-colors"
                    >
                      <div className="ov:shrink-0">
                        <VariantThumb imageUrl={imageUrl} size="lg" />
                      </div>
                      <div className="ov:flex ov:flex-1 ov:min-w-0 ov:justify-between ov:gap-4">
                        <dt className="ov:text-(--ov25-secondary-text-color) ov:capitalize ov:shrink-0">
                          {getString('wizardReviewStepOption', reviewStepStringVars, option.name)}
                        </dt>
                        <dd className="ov:text-(--ov25-secondary-text-color) ov:font-normal ov:text-right ov:truncate">
                          {getString('wizardReviewStepSelection', reviewStepStringVars, value || '—')}
                        </dd>
                      </div>
                    </button>
                  </div>
                );
              })}
            </dl>
            </div>
          </div>
        ) : !isReviewStep && currentOption?.id === 'modules' ? (
        <div data-ov25-wizard-variants-step-content className={`${stepContentClasses} ov25-variant-group-content`}>
          <div
            data-ov25-wizard-variants-content
            className="ov:relative ov:w-full ov:flex ov:flex-col ov:flex-1 ov:min-h-0 ov:overflow-y-auto"
          >
            <Snap2ModulesOptionBody />
          </div>
        </div>
        ) : (
        <div data-ov25-wizard-variants-step-content className={`${stepContentClasses} ov25-variant-group-content`}>
          <div data-ov25-wizard-variants-content className="ov:relative ov:w-full ov:flex ov:flex-col ov:flex-1 ov:min-h-0">
            <div className="ov:relative ov:w-full ov:h-full ov:flex ov:flex-col">
              {filteredOption && allVariants.length === 0 && (
                <div className="ov:flex ov:items-center ov:justify-center ov:h-full ov:p-4">
                  <NoResults />
                </div>
              )}
              {filteredOption && allVariants.length > 0 && (
                <div className="ov-wizard-variants-scroll ov:overflow-y-auto ov:h-full ov:p-4">
                  <div className={`ov:grid ov:content-start ov:gap-2 ${currentOption!.id === 'size' ? 'ov:grid-cols-2!' : 'ov:grid-cols-4!'}`}>
                    <VariantsContent
                      variantsToRender={visibleVariants}
                      VariantCard={currentOption!.id === 'size' ? SizeVariantCard : DefaultVariantCard}
                      isMobile={false}
                      onSelect={(selection) => handleSelectionSelect(selection, currentOption!.id)}
                      showImage={currentOption!.id === 'size' ? true : undefined}
                      showDimensions={currentOption!.id === 'size' ? false : undefined}
                    />
                  </div>
                  {hasMore && (
                    <div
                      ref={(el) => { sentinelRefs.current[currentOption!.id] = el; }}
                      className="ov:h-4 ov:shrink-0"
                      aria-hidden
                    />
                  )}
                </div>
              )}
            </div>

            {isFilterOpenForOption && currentOption && currentOption.id !== 'modules' && (
              <div
                data-open={isFilterOpenForOption}
                className="ov:flex ov:justify-end ov:flex-wrap ov:overflow-y-auto ov:z-8 ov:absolute ov:inset-0 ov:h-full ov:p-2 ov:px-4 ov:bg-(--ov25-background-color) ov:transition-transform ov:duration-500 ov:ease-in-out ov:translate-y-0"
              >
                <FilterContent optionId={currentOption.id} />
              </div>
            )}
          </div>
        </div>
        )}
    </>
  );

  const wizardContent = (
    <div
      ref={rootRef}
      data-ov25-wizard-variants-mode={mode}
      className="ov:w-full ov:flex-1 ov:min-h-0 ov:flex ov:flex-col ov:bg-(--ov25-background-color) ov:overflow-hidden"
      style={mode === 'drawer' && availableHeight != null ? { '--ov25-wizard-variants-available-height': `${availableHeight}px` } as React.CSSProperties : undefined}
    >
      {stepIndicatorBlock}
      {mode !== 'inline' && filterBlock}
      <div className="ov:flex-1 ov:min-h-0 ov:flex ov:flex-col">
        {stepContentOnly}
        {mode !== 'inline' && buttonsBlock}
      </div>
    </div>
  );

  if (mode === 'inline') {
    const inlineNode = (
      <div
        data-ov25-list-variants-mode="inline"
        className="ov:flex ov:flex-col ov:flex-1 ov:min-h-0 ov:h-full ov:overflow-hidden ov:bg-(--ov25-background-color)"
      >
        <div className="ov:shrink-0">
          {stepIndicatorBlock}
          {filterBlock}
        </div>
        <div
          data-ov25-list-variants-content
          className="ov:flex-1 ov:min-h-0 ov:overflow-y-auto ov:pt-0 ov:pb-2"
        >
          {stepContentOnly}
        </div>
        {buttonsBlock}
      </div>
    );
    return isSnap2Mode ? <Snap2VariantSheetColumn>{inlineNode}</Snap2VariantSheetColumn> : inlineNode;
  }

  return isSnap2Mode ? <Snap2VariantSheetColumn>{wizardContent}</Snap2VariantSheetColumn> : wizardContent;
};
